import {
  GroupingStrategy,
  useAutoGroupReservations,
} from "@/api/admin/useAutoGroupReservations";
import { useCreateEventFromReservations } from "@/api/admin/useCreateEventFromReservations";
import { notifications } from "@mantine/notifications";
import { useMemo, useState } from "react";
import { StagingGroup } from "../types";
import { showErrorNotification } from "../utils/errorNotifications";
import { UsePendingRequestsReturn } from "./usePendingRequests";

const STAGING_STORAGE_KEY = "admin-staging-groups";

const generateId = () => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

const createEmptyGroup = (): StagingGroup => ({
  id: generateId(),
  selectedDate: null,
  selectedLocationId: null,
  stagedRequests: [],
});

const loadStagingGroupsFromStorage = (): StagingGroup[] => {
  try {
    const stored = localStorage.getItem(STAGING_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    console.error("Failed to load staging groups from localStorage:", error);
  }
  return [createEmptyGroup(), createEmptyGroup()];
};

const saveStagingGroupsToStorage = (groups: StagingGroup[]) => {
  try {
    localStorage.setItem(STAGING_STORAGE_KEY, JSON.stringify(groups));
  } catch (error) {
    console.error("Failed to save staging groups to localStorage:", error);
  }
};

export interface UseStagingGroupsParams {
  pendingRequestsHook: UsePendingRequestsReturn;
  createEventMutation: ReturnType<typeof useCreateEventFromReservations>;
  autoGroupMutation: ReturnType<typeof useAutoGroupReservations>;
  onEventCreated: () => void;
  onSelectStagingGroup: (groupId: string | null) => void;
}

export interface UseStagingGroupsReturn {
  stagingGroups: StagingGroup[];
  selectedStagingGroup: StagingGroup | null;
  handleAddGroup: () => void;
  handleUpdateDate: (groupId: string, date: string) => void;
  handleUpdateLocation: (groupId: string, locationId: number) => void;
  handleRemoveRequest: (groupId: string, requestId: number) => void;
  handleAddRequestToGroup: (
    groupId: string,
    request: import("@/api/generated/generatedTypes").ReservationRequestDto
  ) => void;
  handleSave: (groupId: string) => void;
  handleDelete: (groupId: string) => void;
  handleCreateEvent: (groupId: string) => void;
  handleSelect: (group: StagingGroup | null) => void;
  handleAutoGroup: (strategy: GroupingStrategy) => void;
  isAutoGrouping: boolean;
}

/**
 * Hook to manage staging groups state and operations
 * Handles CRUD operations, localStorage persistence, and event creation
 */
export function useStagingGroups({
  pendingRequestsHook,
  createEventMutation,
  autoGroupMutation,
  onEventCreated,
  onSelectStagingGroup,
}: UseStagingGroupsParams): UseStagingGroupsReturn {
  const [stagingGroups, setStagingGroups] = useState<StagingGroup[]>(() =>
    loadStagingGroupsFromStorage()
  );
  const [selectedStagingGroupId, setSelectedStagingGroupId] = useState<string | null>(null);

  // Derive selectedStagingGroup from stagingGroups + selectedStagingGroupId (single source of truth)
  const selectedStagingGroup = useMemo(() => {
    if (!selectedStagingGroupId) return null;
    return stagingGroups.find((g) => g.id === selectedStagingGroupId) || null;
  }, [stagingGroups, selectedStagingGroupId]);

  const handleAddGroup = () => {
    setStagingGroups((prev) => [...prev, createEmptyGroup()]);
  };

  const handleUpdateDate = (groupId: string, date: string) => {
    // Normalize date to ISO string for consistent comparison
    const normalizedDate = new Date(date).toISOString();

    setStagingGroups((prev) =>
      prev.map((group) =>
        group.id === groupId ? { ...group, selectedDate: normalizedDate } : group
      )
    );
  };

  const handleUpdateLocation = (groupId: string, locationId: number) => {
    setStagingGroups((prev) =>
      prev.map((group) =>
        group.id === groupId ? { ...group, selectedLocationId: locationId } : group
      )
    );
  };

  const handleRemoveRequest = (groupId: string, requestId: number) => {
    const group = stagingGroups.find((g) => g.id === groupId);
    const removedRequest = group?.stagedRequests.find((req) => req.id === requestId);

    setStagingGroups((prev) =>
      prev.map((g) => {
        if (g.id === groupId) {
          return {
            ...g,
            stagedRequests: g.stagedRequests.filter((req) => req.id !== requestId),
          };
        }
        return g;
      })
    );

    if (removedRequest) {
      pendingRequestsHook.addRequest(removedRequest);
    }
  };

  const handleAddRequestToGroup = (
    groupId: string,
    request: import("@/api/generated/generatedTypes").ReservationRequestDto
  ) => {
    setStagingGroups((prev) =>
      prev.map((g) => {
        if (g.id === groupId) {
          return {
            ...g,
            stagedRequests: [...g.stagedRequests, request],
          };
        }
        return g;
      })
    );

    pendingRequestsHook.removeRequest(request.id);
  };

  const handleSave = (groupId: string) => {
    const existingGroups = loadStagingGroupsFromStorage();
    const groupToSave = stagingGroups.find((g) => g.id === groupId);

    if (!groupToSave) {
      return;
    }

    const existingIndex = existingGroups.findIndex((g) => g.id === groupId);
    if (existingIndex >= 0) {
      existingGroups[existingIndex] = groupToSave;
    } else {
      existingGroups.push(groupToSave);
    }

    saveStagingGroupsToStorage(existingGroups);

    notifications.show({
      title: "저장 완료",
      message: "그룹이 저장되었습니다",
      color: "green",
    });
  };

  const handleDelete = (groupId: string) => {
    const groupToDelete = stagingGroups.find((g) => g.id === groupId);
    if (groupToDelete) {
      groupToDelete.stagedRequests.forEach((request) => {
        pendingRequestsHook.addRequest(request);
      });
    }
    const updatedGroups = stagingGroups.filter((g) => g.id !== groupId);
    setStagingGroups(updatedGroups);
    saveStagingGroupsToStorage(updatedGroups);
    if (selectedStagingGroupId === groupId) {
      setSelectedStagingGroupId(null);
    }
  };

  const handleCreateEvent = (groupId: string) => {
    const group = stagingGroups.find((g) => g.id === groupId);
    if (!group) return;

    if (!group.selectedDate || !group.selectedLocationId || group.stagedRequests.length === 0) {
      notifications.show({
        title: "입력 필요",
        message: "날짜, 장소, 최소 1명의 요청이 필요합니다",
        color: "yellow",
      });
      return;
    }

    createEventMutation.mutate(
      {
        startTime: group.selectedDate,
        capacity: 6,
        reservationRequestIds: group.stagedRequests.map((r) => r.id),
        locationId: group.selectedLocationId,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: "이벤트 생성 완료",
            message: "이벤트가 성공적으로 생성되었습니다!",
            color: "green",
          });
          const updatedGroups = stagingGroups.filter((g) => g.id !== groupId);
          setStagingGroups(updatedGroups);
          saveStagingGroupsToStorage(updatedGroups);
          if (selectedStagingGroupId === groupId) {
            setSelectedStagingGroupId(null);
          }
          onEventCreated();
        },
        onError: (error) => {
          showErrorNotification(error, "이벤트 생성 실패");
        },
      }
    );
  };

  const handleSelect = (group: StagingGroup | null) => {
    const groupId = group?.id || null;
    setSelectedStagingGroupId(groupId);
    onSelectStagingGroup(groupId);
  };

  const handleAutoGroup = (strategy: GroupingStrategy) => {
    autoGroupMutation.mutate({ strategy }, {
      onSuccess: (data) => {
        const newGroups: StagingGroup[] = data.suggestedGroups.map((suggestedGroup) => {
          const locationId = suggestedGroup.metadata.locationIds[0] || null;
          let selectedDate: string | null = null;
          if (suggestedGroup.metadata.commonTimeSlots.length > 0) {
            const timeSlot = suggestedGroup.metadata.commonTimeSlots[0];
            selectedDate = new Date(timeSlot).toISOString();
          }

          return {
            id: suggestedGroup.id,
            selectedDate,
            selectedLocationId: locationId,
            stagedRequests: suggestedGroup.requests,
            isAutoGenerated: true,
            metadata: suggestedGroup.metadata,
          };
        });

        setStagingGroups((prev) => [...newGroups, ...prev]);

        newGroups.forEach((group) => {
          group.stagedRequests.forEach((request) => {
            pendingRequestsHook.removeRequest(request.id);
          });
        });

        notifications.show({
          title: "자동 그룹 생성 완료",
          message: `${data.suggestedGroups.length}개의 그룹이 생성되었습니다`,
          color: "green",
        });
      },
      onError: (error) => {
        showErrorNotification(error, "자동 그룹 생성 실패");
      },
    });
  };

  return {
    stagingGroups,
    selectedStagingGroup,
    handleAddGroup,
    handleUpdateDate,
    handleUpdateLocation,
    handleRemoveRequest,
    handleAddRequestToGroup,
    handleSave,
    handleDelete,
    handleCreateEvent,
    handleSelect,
    handleAutoGroup,
    isAutoGrouping: autoGroupMutation.isPending,
  };
}
